import { Fragment, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/**
 * How a column behaves once there is no room for a table.
 *
 * Horizontal scrolling keeps a table *reachable* on a phone, but a 7-column
 * user search still means swiping to read one row. Below `md` we stop drawing
 * a table at all and draw one card per row, and these tell the card which part
 * of itself each column is.
 *
 *   title     the card's headline (one per column set)
 *   subtitle  the line under the headline
 *   meta      pushed to the headline's right — badges, statuses, amounts
 *   actions   pinned to the card's footer
 *   row       default: a label/value pair in the card body
 *   hidden    dropped from the card; still shown in the desktop table
 */
export type MobileSlot = "title" | "subtitle" | "meta" | "actions" | "row" | "hidden";

export type Column<T> = {
  /** Stable identity for React keys. */
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  mobile?: MobileSlot;
  /** Label used in the card body; falls back to `header` when it is a string. */
  mobileLabel?: string;
  className?: string;
  headClassName?: string;
};

export type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T, index: number) => string | number;
  loading?: boolean;
  error?: unknown;
  /** Shown when there are no rows. */
  empty?: ReactNode;
  /** Rendered under both the table and the card list — pagination, totals. */
  footer?: ReactNode;
  errorMessage?: string;
  className?: string;
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  empty = "Nothing to show.",
  footer,
  errorMessage = "Could not load this list.",
  className,
}: DataTableProps<T>) {
  if (loading) return <DataTableSkeleton columns={columns.length} />;
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-red-300">
        {errorMessage}
      </div>
    );
  }
  if (!rows) return null;

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        {empty}
      </div>
    );
  }

  const slot = (c: Column<T>): MobileSlot => c.mobile ?? "row";
  const titleCol = columns.find((c) => slot(c) === "title") ?? columns[0];
  const subtitleCols = columns.filter((c) => slot(c) === "subtitle");
  const metaCols = columns.filter((c) => slot(c) === "meta");
  const actionCols = columns.filter((c) => slot(c) === "actions");
  const bodyCols = columns.filter((c) => c !== titleCol && slot(c) === "row");

  return (
    <div className={cn("space-y-3", className)}>
      {/* ── Phone: one card per row ─────────────────────────────────── */}
      <ul className="space-y-2 md:hidden">
        {rows.map((row, i) => (
          <li
            key={rowKey(row, i)}
            className="rounded-lg border border-border bg-card p-3 text-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-0.5">
                <div className="break-words font-medium">{titleCol.cell(row)}</div>
                {subtitleCols.map((c) => (
                  <div key={c.key} className="break-words text-xs text-muted-foreground">
                    {c.cell(row)}
                  </div>
                ))}
              </div>
              {metaCols.length > 0 && (
                <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                  {metaCols.map((c) => (
                    <div key={c.key} className="flex items-center gap-1.5">
                      {/* One meta value is self-evident from context. Two are
                          not: the outbox card showed "processed" above
                          "failed" with nothing saying which lane each belonged
                          to, on the one page whose entire job is that
                          distinction. */}
                      {metaCols.length > 1 && (
                        <span className="text-muted-foreground">{labelOf(c)}</span>
                      )}
                      {c.cell(row)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {bodyCols.length > 0 && (
              <dl className="mt-3 grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-1.5 border-t border-border pt-3 text-xs">
                {bodyCols.map((c) => (
                  <Fragment key={c.key}>
                    <dt className="text-muted-foreground">{labelOf(c)}</dt>
                    <dd className="min-w-0 break-words text-right">{c.cell(row)}</dd>
                  </Fragment>
                ))}
              </dl>
            )}

            {actionCols.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                {actionCols.map((c) => (
                  <Fragment key={c.key}>{c.cell(row)}</Fragment>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* ── Tablet and up: the table, always inside its scroll container ── */}
      <div className="hidden overflow-hidden rounded-lg border border-border md:block">
        <Table>
          <TableHeader className="bg-muted/40">
            <TableRow className="hover:bg-transparent">
              {columns.map((c) => (
                <TableHead key={c.key} className={c.headClassName}>
                  {c.header || <span className="sr-only">{labelOf(c)}</span>}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={rowKey(row, i)}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={c.className}>
                    {c.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {footer && <div className="text-xs text-muted-foreground">{footer}</div>}
    </div>
  );
}

function labelOf<T>(c: Column<T>): string {
  if (c.mobileLabel) return c.mobileLabel;
  if (typeof c.header === "string" && c.header.trim()) return c.header;
  // An action column carries no visible header. Name it for the screen reader
  // rather than leaking the internal column key.
  if (c.mobile === "actions") return "Actions";
  return c.key;
}

export function DataTableSkeleton({ columns = 4, rows = 5 }: { columns?: number; rows?: number }) {
  return (
    <div className="space-y-2">
      <div className="space-y-2 md:hidden">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
      <div className="hidden rounded-lg border border-border md:block">
        <div className="flex gap-3 border-b border-border p-3">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} className="h-4 flex-1" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-3 border-b border-border p-3 last:border-0">
            {Array.from({ length: columns }).map((_, i) => (
              <Skeleton key={i} className="h-4 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
